<script setup>
import { usePage } from "@inertiajs/vue3";
import MarketingLayout from "@/Layouts/MarketingLayout.vue";
import ModuleCards from "@/Components/Marketing/ModuleCards.vue";
import Faq from "@/Components/Marketing/Faq.vue";
import TrustBadges from "@/Components/Marketing/TrustBadges.vue";

const page = usePage();

const modules = [
    {
        slug: "timesheets",
        name: "Timesheets",
        tagline: "Uren, kilometers en kosten",
        description: "Volg uren, verplaatsingen en onkosten en factureer ze rechtstreeks door naar Teamleader. Geen dubbele invoer meer.",
        features: [
            "Mobiele registratie onderweg",
            "Automatische facturatie via Teamleader",
            "Inzicht in marges en teamprestaties",
        ],
        href: page.props.timesheetsUrl,
    },
    {
        slug: "sync",
        name: "Sync",
        tagline: "Workflows zonder code",
        description: "Koppel Teamleader met andere tools via betrouwbare workflows. Triggers, herpogingen en duidelijke logs zijn standaard.",
        features: [
            "Kant-en-klare templates",
            "Automatische herpogingen bij fouten",
            "Volledig logboek per uitvoering",
        ],
        href: page.props.syncUrl,
    },
    {
        slug: "assign",
        name: "Assign",
        tagline: "Slimme leadverdeling",
        description: "Verdeel nieuwe Teamleader-leads automatisch over uw verkoopteam op basis van pipeline, land en eigen regels.",
        features: [
            "Regels per markt of segment",
            "Standaard verantwoordelijke als fallback",
            "Volledige historiek per toewijzing",
        ],
        href: page.props.assignUrl,
    },
];

const faqItems = [
    {
        q: "Wat is besapro precies?",
        a: "Besapro is een suite van drie losse modules (Timesheets, Sync en Assign) die elk Teamleader uitbreiden. U kiest welke module u nodig heeft.",
    },
    {
        q: "Werken de modules ook los van elkaar?",
        a: "Ja. Elke module is volledig zelfstandig. U kunt starten met één module en later uitbreiden zonder dat er iets verandert aan uw bestaande setup.",
    },
    {
        q: "Voor wie is besapro bedoeld?",
        a: "Voor Belgische en Nederlandse KMO's die met Teamleader Focus werken en hun administratie, koppelingen of leadverdeling willen automatiseren.",
    },
    {
        q: "Hoe werkt de koppeling met Teamleader?",
        a: "U koppelt uw Teamleader-account éénmalig via OAuth. Daarna wisselen besapro en Teamleader automatisch data uit volgens de regels die u instelt.",
    },
    {
        q: "Waar staat onze data?",
        a: "Alle data wordt veilig gehost binnen de Europese Unie en is volledig conform de AVG-wetgeving. We werken alleen met Europese providers.",
    },
    {
        q: "Hoe kan ik besapro proberen?",
        a: "Maak een account aan op de module die u wilt testen. U kunt op elk moment een demo aanvragen via info@besapro.eu, we helpen u graag bij de eerste stappen.",
    },
];
</script>

<template>
  <MarketingLayout
    :menu="true"
    title="Welkom"
  >
    <section class="overflow-hidden bg-white py-24 sm:py-32">
      <div class="mx-auto max-w-7xl md:px-6 lg:px-8">
        <div class="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-center">
          <div class="px-6 lg:px-0 lg:pt-4 lg:pr-4">
            <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
              <h2 class="text-base/7 font-semibold text-indigo-600">
                Voor Teamleader-gebruikers
              </h2>
              <p class="mt-2 text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
                Haal meer uit Teamleader.
              </p>
              <p class="mt-6 text-lg/8 text-gray-600">
                Besapro is een suite van drie losse modules, elk gebouwd om Teamleader uit te breiden voor Belgische en Nederlandse KMO's. Kies wat u nodig heeft.
              </p>
              <div class="mt-10 flex flex-wrap items-center gap-4">
                <a
                  :href="route('marketing.pricing')"
                  class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Bekijk de prijzen
                </a>
                <a
                  href="mailto:info@besapro.eu?subject=Demo&body=Ik%20wil%20graag%20een%20demo%20voor%20besapro"
                  class="inline-flex items-center rounded-md border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-900 hover:border-gray-400"
                >
                  Vraag een demo aan
                </a>
              </div>
            </div>
          </div>
          <div class="sm:px-6 lg:px-0">
            <div class="relative isolate overflow-hidden bg-indigo-500 px-6 pt-8 sm:mx-auto sm:max-w-2xl sm:rounded-3xl sm:pt-16 sm:pr-0 sm:pl-16 lg:mx-0 lg:max-w-none">
              <div
                class="absolute -inset-y-px -left-3 -z-10 w-full origin-bottom-left skew-x-[-30deg] bg-indigo-100 opacity-20 ring-1 ring-white ring-inset"
                aria-hidden="true"
              />
              <div class="mx-auto max-w-2xl sm:mx-0 sm:max-w-none">
                <img
                  src="/images/dashboard.png"
                  alt="besapro dashboard"
                  loading="eager"
                  class="-mb-12 w-2xl max-w-none rounded-tl-xl bg-gray-800 ring-1 ring-white/10"
                >
              </div>
              <div
                class="pointer-events-none absolute inset-0 ring-1 ring-black/10 ring-inset sm:rounded-3xl"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <ModuleCards :modules="modules" />

    <Faq
      :items="faqItems"
      intro="Alles wat u wilt weten voor u start."
    />

    <TrustBadges />
  </MarketingLayout>
</template>
